import axios from 'axios';
import { getDeviceId } from '../utils/deviceInfo';
const baseURL = import.meta.env.VITE_API_URL ?? '';
export const api = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});
api.interceptors.request.use((config) => {
    const did = getDeviceId();
    config.headers['x-device-id'] = did;
    return config;
});
let isRefreshing = false;
let failedQueue = [];
/**
 * Processes the queue of paused API requests.
 * DANGER: Do not modify the try/catch/finally logic in this function!
 * If a Promise in this queue is not explicitly resolved or rejected,
 * the React component waiting for it will hang infinitely in a "Loading" state.
 */
const processQueue = (error, token = null) => {
    try {
        failedQueue.forEach(prom => {
            try {
                if (error) {
                    prom.reject(error);
                }
                else {
                    prom.resolve(token);
                }
            }
            catch (innerErr) {
                console.error('[Auth] Error resolving queued promise', innerErr);
                prom.reject(innerErr);
            }
        });
    }
    finally {
        // ALWAYS clear the queue, even if an exception occurs during iteration,
        // to prevent memory leaks and infinite loops on subsequent refreshes.
        failedQueue = [];
    }
};
api.interceptors.response.use((response) => response, async (err) => {
    const originalRequest = err.config;
    if (err.response?.status === 401) {
        const errorData = err.response.data;
        if (errorData?.error === 'SESSION_REVOKED') {
            window.dispatchEvent(new Event('auth-logout'));
            return Promise.reject(err);
        }
        // If we are already trying to hit the refresh route and it fails, it means the refresh token is dead
        if (originalRequest.url?.includes('/auth/refresh') || originalRequest.url?.includes('/auth/login')) {
            if (originalRequest.url?.includes('/auth/refresh')) {
                window.dispatchEvent(new Event('auth-logout'));
            }
            return Promise.reject(err);
        }
        if (!originalRequest._retry) {
            if (isRefreshing) {
                // If a refresh is already happening, queue this request
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                }).then(() => {
                    return api(originalRequest);
                }).catch((err) => {
                    return Promise.reject(err);
                });
            }
            originalRequest._retry = true;
            isRefreshing = true;
            try {
                // Attempt silent refresh
                await api.post('/auth/refresh');
                isRefreshing = false;
                processQueue(null);
                // Wait for the new token cookie to be set, then replay the request
                return api(originalRequest);
            }
            catch (refreshErr) {
                isRefreshing = false;
                processQueue(refreshErr, null);
                window.dispatchEvent(new Event('auth-logout'));
                return Promise.reject(refreshErr);
            }
        }
    }
    return Promise.reject(err);
});
export default api;
